import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useNavigate, useSearchParams } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import { createSubscriptionIntent, upgradeSubscriptionIntent } from '../api/billingReader'
import { getCreatorPricing, type CreatorPricingPlan, type StripePlanPrice } from '../api/pricing'

const stripePublicKey = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null

type Tier = 'creator' | 'creator_pro'
type Interval = 'month'

export function normalizeTier(value: string | null): Tier {
  return value === 'creator_pro' ? 'creator_pro' : 'creator'
}

export function normalizeInterval(_value: string | null): Interval {
  return 'month'
}

export function safeOrigin(): string {
  if (typeof window === 'undefined') return ''
  return String(window.location?.origin || '').replace(/\/$/, '')
}

function displayPrice(price: StripePlanPrice | null): string {
  if (!price || price.amount_cents == null || !price.currency) return 'Pricing unavailable'
  const label = (price.amount_cents / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: String(price.currency || 'usd').toUpperCase(),
    maximumFractionDigits: 2,
  })
  return `${label}/mo`
}

function CheckoutForm({
  onError,
  returnUrl,
}: {
  onError: (message: string) => void
  returnUrl: string
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    onError('')
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    })
    if (result.error) {
      onError(result.error.message || 'Unable to complete checkout.')
      setSubmitting(false)
      return
    }
    setSubmitting(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || !elements || submitting}
        className="w-full rounded-xl bg-brand px-4 py-2 font-semibold disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? 'Subscribing...' : 'Subscribe'}
      </button>
      <div className="text-xs text-white/60">Powered by Stripe</div>
    </form>
  )
}

export default function SubscribePage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const tier = normalizeTier(params.get('tier'))
  const interval = normalizeInterval(params.get('interval'))
  const [pricing, setPricing] = useState<CreatorPricingPlan[]>([])
  const [clientSecret, setClientSecret] = useState('')
  const [subscriptionId, setSubscriptionId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoading(true)
      setError('')
      try {
        const plansPayload = await getCreatorPricing()
        if (cancelled) return
        setPricing(plansPayload?.plans || [])
        const sub = await createSubscriptionIntent({ tier, interval })
        if (cancelled) return
        setClientSecret(sub.client_secret || '')
        setSubscriptionId(sub.subscription_id || '')
        if (!sub.client_secret && sub.subscription_id && sub.requires_confirmation === false) {
          navigate(`/subscribe/success?subscription_id=${encodeURIComponent(sub.subscription_id)}&tier=${tier}`, { replace: true })
          return
        }
      } catch (e: any) {
        if (cancelled) return
        const statusCode = Number(e?.response?.status || 0)
        const code = String(e?.response?.data?.code || '')
        if (statusCode === 401) {
          navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)
          return
        }
        if (statusCode === 409 && code === 'UPGRADE_REQUIRED') {
          try {
            const upgraded = await upgradeSubscriptionIntent({ tier, interval })
            if (cancelled) return
            setClientSecret(upgraded.client_secret || '')
            setSubscriptionId(upgraded.subscription_id || '')
            if (!upgraded.client_secret && upgraded.subscription_id && upgraded.requires_confirmation === false) {
              navigate(`/subscribe/success?subscription_id=${encodeURIComponent(upgraded.subscription_id)}&tier=${tier}`, { replace: true })
              return
            }
          } catch (upgradeErr: any) {
            if (cancelled) return
            setError(upgradeErr?.response?.data?.detail || upgradeErr?.message || 'Unable to initialize upgrade checkout.')
          }
        } else if (statusCode === 409 && (code === 'SUBSCRIPTION_ALREADY_ACTIVE' || code === 'ACTIVE_SUBSCRIPTION_EXISTS')) {
          setError('You already have this plan active. Manage billing to change your subscription.')
        } else {
          setError(e?.response?.data?.detail || e?.message || 'Unable to initialize checkout.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [tier, interval])

  const selectedPlan = useMemo(() => {
    const found = pricing.find((p) => p.tier === tier) || null
    return {
      name: found?.name || (tier === 'creator' ? 'Creator' : 'Creator Pro'),
      price: found?.prices?.monthly ?? null,
      features:
        tier === 'creator'
          ? ['Publish with Pixe Publish', 'Earn PIXE from gifts and subscriptions', 'Withdraw available earnings']
          : ['Everything in Creator', 'Pro-level creator limits', 'Priority support and advanced workflows'],
    }
  }, [pricing, tier])

  const origin = safeOrigin()
  const returnUrl = `${origin}/subscribe/success?subscription_id=${encodeURIComponent(subscriptionId)}&tier=${tier}`
  const stripeUnavailable = !stripePromise

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-3xl font-extrabold">Complete your {selectedPlan.name} subscription</h1>
          <button
            type="button"
            onClick={() => navigate('/pricing')}
            className="rounded-lg border border-white/15 px-3 py-1.5 text-sm text-white/80 hover:bg-white/5"
          >
            Back
          </button>
        </div>

        {stripeUnavailable ? (
          <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
            Stripe not configured. Subscription checkout is unavailable.
          </div>
        ) : null}
        {error ? <div className="mb-4 rounded-xl border border-red-500/40 p-3 text-sm text-red-300">{error}</div> : null}

        <div className="grid gap-4 md:grid-cols-2">
          <section className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-white/60">Plan</div>
            <div className="mt-2 text-2xl font-bold">{selectedPlan.name}</div>
            <div className="mt-1 text-lg text-white/80">{displayPrice(selectedPlan.price)}</div>
            <ul className="mt-4 space-y-2 text-sm text-white/85">
              {selectedPlan.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <i className="fa-solid fa-check mt-1 text-emerald-400" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="mb-3 text-sm font-semibold text-white/80">Payment details</div>
            {loading ? (
              <div className="rounded-xl border border-white/10 bg-black/30 p-4 text-sm text-white/70">Loading checkout...</div>
            ) : stripePromise && clientSecret && subscriptionId ? (
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' as const } }}>
                <CheckoutForm onError={setError} returnUrl={returnUrl} />
              </Elements>
            ) : (
              <div className="rounded-xl border border-white/10 bg-black/30 p-4 text-sm text-white/70">
                Checkout unavailable right now. You can manage your existing plan in Billing.
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}
